import React, { useState, useEffect, useRef } from 'react';
import ListErrors from './ListErrors';
import agent from '../agent';
import { connect } from 'react-redux';
import {
  SETTINGS_SAVED,
  SETTINGS_PAGE_UNLOADED,
  LOGOUT,
  UPLOAD_USER_IMAGE,
  UPLOAD_USER_BACKGROUND_IMAGE,
  THEME_MODE_CHANGE,
} from '../constants/actionTypes';
import FileDropzoneArea from './FileDropzoneArea';
import { convertToBase64 } from '../utils/imageUtils';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useTranslation from '../customHooks/translations';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { useLanguageContext } from '../contexts/LanguageContext';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PortraitIcon from '@mui/icons-material/Portrait';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import TranslateIcon from '@mui/icons-material/Translate';
import { ReactComponent as FreaklandIcon } from '../assets/freakland.svg';
import SocialListComponent from './List/SocialListComponent';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import DeleteIcon from '@mui/icons-material/Delete';
import { DEFAULT_THEME_COLOR, DEFAULT_THEME_COLOR_GREY, DEFAULT_GRADIENT, DEFAULT_GRADIENT_HOVER } from '../constants/theme';
import AddIcon from '@mui/icons-material/Add';
import { validateYouTubeFeed } from '../utils/freakUtils';
import { SOCIAL_PLATFORMS } from '../utils/socialLinks';
import ShareIcon from '@mui/icons-material/Share';
import packageJson from '../../package.json';
import SettingsIcon from '@mui/icons-material/Settings';
import Button from '@mui/material/Button';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { setupNotifications } from '../firebase';
import Box from '@mui/material/Box';
import ContrastIcon from '@mui/icons-material/Contrast';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import YouTubeIcon from '@mui/icons-material/YouTube';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import Switch from '@mui/material/Switch';
import { useMediaQuery } from 'react-responsive';
import Divider from '@mui/material/Divider';
import BlockIcon from '@mui/icons-material/Block';
import BlockedUsersList from './UserSafety/BlockedUsersList';
import useFreaksT from './TheFreaks/useFreaksT';
import {
  FREAKS_CHALLENGE_PREFERENCE,
  FREAKS_DAILY_PREFERENCE,
  FREAKS_INVITE_PREFERENCE,
  FREAKS_PREFERENCE_CONTEXT,
  FREAKS_PROFILE_PREFERENCE,
} from '../utils/theFreaks';

// ─── Shared styles ────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: 'white',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.18)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.35)' },
    '&.Mui-focused fieldset': { borderColor: DEFAULT_THEME_COLOR },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.45)' },
  '& .MuiInputLabel-root.Mui-focused': { color: DEFAULT_THEME_COLOR },
  '& .MuiFormHelperText-root': { color: '#f87171' },
};

const accordionSx = {
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid rgba(255,255,255,0.07)',
  borderRadius: '12px !important',
  mb: 1.5,
  boxShadow: 'none',
  '&:before': { display: 'none' },
  '& .MuiAccordionSummary-root': { borderRadius: '12px', minHeight: 52 },
  '& .MuiAccordionSummary-expandIconWrapper': { color: 'rgba(255,255,255,0.4)' },
};

const SectionIcon = ({ children }) => (
  <Box sx={{ color: 'rgba(255,255,255,0.55)', display: 'flex', alignItems: 'center', mr: 1.5, flexShrink: 0 }}>
    {children}
  </Box>
);

const radioSx = {
  color: 'rgba(255,255,255,0.35)',
  '&.Mui-checked': { color: DEFAULT_THEME_COLOR },
  p: 0.5,
};

const CONTEXTS = [
  { value: 'wof', labelKey: 'contextWof' },
  { value: 'bike', labelKey: 'contextBike' },
  { value: 'fishing', labelKey: 'contextFishing' },
  { value: 'trip', labelKey: 'contextTrip' },
  { value: 'fit', labelKey: 'contextFit' },
  { value: 'dogs', labelKey: 'contextDogs' },
  { value: 'games', labelKey: 'contextGames' },
  { value: 'books', labelKey: 'contextBooks' },
  { value: 'food', labelKey: 'contextFood' },
  { value: 'shopping', labelKey: 'contextShopping' },
  { value: 'tech', labelKey: 'contextTech' },
  { value: 'motorization', labelKey: 'contextMotorization' },
  { value: 'gardening', labelKey: 'contextGardening' },
  { value: 'music', labelKey: 'contextMusic' },
  { value: 'sport', labelKey: 'contextSport' },
  { value: 'winterSports', labelKey: 'contextWinterSports' },
  { value: 'horseRiding', labelKey: 'contextHorseRiding' },
  { value: 'movies', labelKey: 'contextMovies' },
  { value: 'series', labelKey: 'contextSeries' },
  { value: 'skateboard', labelKey: 'contextSkateboard' },
  { value: 'inlineSkate', labelKey: 'contextInlineSkate' },
  { value: 'scooter', labelKey: 'contextScooter' },
  { value: 'bmx', labelKey: 'contextBmx' },
  { value: 'mushroom', labelKey: 'contextMushroom' },
  { value: 'nordicWalking', labelKey: 'contextNordicWalking' },
];

// Custom (opt-in/opt-out) notifications stored as UserNotificationPreference rows.
// Default is ON for users whose main context matches, OFF otherwise.
// One radar per context; mushroom/fishing have their own names.
const RADAR_LABEL_OVERRIDES = {
  mushroom: 'mushroomRadarNotification',
  fishing: 'fishingNearbyNotification',
};
const CUSTOM_NOTIFICATIONS = CONTEXTS.map(({ value, labelKey }) => ({
  key: value,
  context: value,
  type: 'NEARBY_FREAK',
  labelKey: RADAR_LABEL_OVERRIDES[value] ?? labelKey,
}));

// The Freaks: three push types + showing my game card on my profile. ON unless turned off (same rows as mobile).
const FREAKS_PREFERENCES = [
  { key: 'theFreaksDaily', type: FREAKS_DAILY_PREFERENCE, labelKey: 'settings.theFreaksDailyNotification' },
  { key: 'theFreaksChallenge', type: FREAKS_CHALLENGE_PREFERENCE, labelKey: 'settings.theFreaksChallengeNotification' },
  { key: 'theFreaksInvite', type: FREAKS_INVITE_PREFERENCE, labelKey: 'settings.theFreaksInviteNotification' },
  {
    key: 'theFreaksOnProfile',
    type: FREAKS_PROFILE_PREFERENCE,
    labelKey: 'settings.theFreaksOnProfile',
    descriptionKey: 'settings.theFreaksOnProfileDescription',
  },
];
const FREAKS_PREFERENCE_DEFAULTS = FREAKS_PREFERENCES.reduce((acc, { key }) => ({ ...acc, [key]: true }), {});

const getCustomNotificationDefaults = (userContext) =>
  CUSTOM_NOTIFICATIONS.reduce(
    (acc, { key, context }) => ({ ...acc, [key]: userContext === context }),
    {}
  );

// ─── Component ────────────────────────────────────────────────────────────────

export const Settings = (props) => {
  const translation = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { handleChangeLanguage } = useLanguageContext();
  const isDesktop = useMediaQuery({ minWidth: 992 });
  const feedYoutube = location?.state?.youtube || null;

  const [image, setImage] = useState('');
  const [currentImage, setCurrentImage] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [backgroundImage, setBackgroundImage] = useState('');
  const [currentBackgroundImage, setCurrentBackgroundImage] = useState('');
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [username, setUsername] = useState('');
  const [bio, setBio] = useState('');
  const [freaksCount, setFreaksCount] = useState(0);
  const [points, setPoints] = useState(0);
  const [followedByCount, setFollowedByCount] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [email, setEmail] = useState('');
  const [language, setLanguage] = useState('');
  const [context, setContext] = useState('');
  const [themeMode, setThemeMode] = useState('dark');
  const [socialLinks, setSocialLinks] = useState({});
  const [isValidYouTubeFeed, setIsValidYouTubeFeed] = useState(true);
  const [youTubeFeed, setYouTubeFeed] = useState('');
  const [isAddingFeed, setIsAddingFeed] = useState(false);
  const [customNotifications, setCustomNotifications] = useState(() =>
    getCustomNotificationDefaults(props.currentUser?.context)
  );
  const [freaksPreferences, setFreaksPreferences] = useState(FREAKS_PREFERENCE_DEFAULTS);
  const { t: freaksT } = useFreaksT();

  const paddingLeft = (isDesktop && props.leftNavigationOpen) ? 300 : (isDesktop && !props.leftNavigationOpen ? 75 : 0);

  const youtubeFeedSectionRef = useRef(null);

  useEffect(() => {
    setCurrentImage(props.currentUser?.image ?? '');
    setCurrentBackgroundImage(props.currentUser?.backgroundImage ?? '');
  }, []);

  useEffect(() => {
    setUsername(props.currentUser?.username ?? '');
    setBio(props.currentUser?.bio ?? '');
    setFreaksCount(props.currentUser?.freaksCount ?? 0);
    setPoints(props.currentUser?.points ?? 0);
    setFollowedByCount(props.currentUser?.followedByCount ?? 0);
    setFollowingCount(props.currentUser?.followingCount ?? 0);
    setEmail(props.currentUser?.email ?? '');
    setImage(props.currentUser?.image ?? '');
    setImageUrl(props.currentUser?.imageUrl ?? '');
    setBackgroundImage(props.currentUser?.backgroundImage ?? '');
    setBackgroundImageUrl(props.currentUser?.backgroundImageUrl ?? '');
    setLanguage(props.currentUser?.language ?? '');
    setContext(props.currentUser?.context ?? '');
    setThemeMode(props.currentUser?.themeMode ?? 'dark');
    setSocialLinks(
      SOCIAL_PLATFORMS.reduce(
        (acc, { key }) => ({ ...acc, [key]: props.currentUser?.[key] ?? '' }),
        {}
      )
    );
  }, [props.currentUser?.image, props.currentUser?.backgroundImage, isAddingFeed]);

  useEffect(() => {
    if (feedYoutube && youtubeFeedSectionRef.current) {
      youtubeFeedSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [feedYoutube]);

  useEffect(() => {
    // context-based defaults; overridden by stored preferences when a row exists
    setCustomNotifications(getCustomNotificationDefaults(props.currentUser?.context));
    agent.NotificationPreferences.list().then((prefs) => {
      setCustomNotifications((current) => {
        const next = { ...current };
        CUSTOM_NOTIFICATIONS.forEach(({ key, context, type }) => {
          const pref = prefs.find(p => p.context === context && p.type === type);
          if (pref !== undefined) next[key] = pref.enabled;
        });
        return next;
      });
      setFreaksPreferences(() => {
        const next = { ...FREAKS_PREFERENCE_DEFAULTS };
        FREAKS_PREFERENCES.forEach(({ key, type }) => {
          const pref = prefs.find(p => p.context === FREAKS_PREFERENCE_CONTEXT && p.type === type);
          if (pref !== undefined) next[key] = pref.enabled;
        });
        return next;
      });
    }).catch(() => {});
  }, [props.currentUser?.context]);

  const handleCustomNotificationToggle = async ({ key, context, type }, enabled) => {
    setCustomNotifications((current) => ({ ...current, [key]: enabled }));
    try {
      await agent.NotificationPreferences.update(context, type, enabled);
    } catch {
      setCustomNotifications((current) => ({ ...current, [key]: !enabled }));
    }
  };

  const handleFreaksPreferenceToggle = async ({ key, type }, enabled) => {
    setFreaksPreferences((current) => ({ ...current, [key]: enabled }));
    try {
      await agent.NotificationPreferences.update(FREAKS_PREFERENCE_CONTEXT, type, enabled);
    } catch {
      setFreaksPreferences((current) => ({ ...current, [key]: !enabled }));
    }
  };

  const addFeedYouTube = async (feed) => {
    setIsAddingFeed(true);
    try {
      await agent.Feeds.createUserFeedForYouTube(feed);
      setIsValidYouTubeFeed(true);
      setYouTubeFeed('');
      setIsAddingFeed(false);
    } catch {
      setIsValidYouTubeFeed(false);
      setIsAddingFeed(false);
    }
  };

  const submitForm = (ev) => {
    ev.preventDefault();
    const user = { username, bio, email, language, context, themeMode };
    SOCIAL_PLATFORMS.forEach(({ key }) => {
      user[key] = (socialLinks[key] ?? '').trim();
    });
    if (image && image !== currentImage) user.image = image;
    if (backgroundImage && backgroundImage !== currentBackgroundImage) user.backgroundImage = backgroundImage;
    props.onSubmitForm(user);
  };

  const handleThemeToggle = () => {
    const next = props.handleTheme();
    setThemeMode(next);
    props.onThemeModeChange(next);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0d1b2a 0%, #091420 100%)',
        paddingLeft: `${paddingLeft}px`,
      }}
    >
      {/* ── Sticky header ── */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 1.5,
          background: 'rgba(13,27,42,0.92)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton
            onClick={() => navigate(-1)}
            size="small"
            sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: 'white' } }}
          >
            <ArrowBackIcon />
          </IconButton>
          <SettingsIcon sx={{ color: DEFAULT_THEME_COLOR, fontSize: 20 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '1rem' }}>
            {translation.settings.title}
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={submitForm}
          sx={{
            background: DEFAULT_GRADIENT,
            color: 'white',
            fontWeight: 700,
            px: 3,
            flexShrink: 0,
            '&:hover': { background: DEFAULT_GRADIENT_HOVER },
          }}
        >
          {translation.settings.update}
        </Button>
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
        <ListErrors errors={props.errors} />

        {/* ── Profile card ── */}
        <Box
          sx={{
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.07)',
            mb: 3,
          }}
        >
          {/* Banner */}
          <Box
            sx={{
              height: 110,
              background: backgroundImageUrl
                ? `url("${backgroundImageUrl}") center/cover`
                : 'linear-gradient(135deg, rgba(54,201,162,0.25) 0%, rgba(79,172,254,0.25) 100%)',
            }}
          />
          {/* Info */}
          <Box sx={{ px: 2.5, pb: 2.5, background: 'rgba(255,255,255,0.03)' }}>
            <Avatar
              alt={username}
              src={imageUrl}
              sx={{
                width: 72,
                height: 72,
                border: `3px solid ${DEFAULT_THEME_COLOR}`,
                mt: -4.5,
                mb: 1,
              }}
            />
            <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.1rem', lineHeight: 1.2 }}>
              {username}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', mb: 1.5 }}>
              @{username}
            </Typography>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 3, mb: bio ? 1.5 : 0 }}>
              {[
                { count: freaksCount, label: translation.profile.freaks },
                { count: points, label: translation.profile.points },
                { count: followedByCount, label: translation.profile.followers, onClick: () => navigate(`/followed-by/@${username}`) },
                { count: followingCount, label: translation.profile.following, onClick: () => navigate(`/following/@${username}`) },
              ].map(({ count, label, onClick }) => (
                <Box
                  key={label}
                  onClick={onClick}
                  sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { opacity: 0.75 } : {} }}
                >
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem', lineHeight: 1 }}>
                    {count}
                  </Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.72rem' }}>
                    {label}
                  </Typography>
                </Box>
              ))}
            </Box>

            {bio && (
              <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', mt: 1 }}>
                {bio}
              </Typography>
            )}
          </Box>
        </Box>

        {/* ── Avatar photo ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><PortraitIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.userImageLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <FileDropzoneArea onFileUpload={props.onFileUpload} />
          </AccordionDetails>
        </Accordion>

        {/* ── Background image ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><WallpaperIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.backgroundImageLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <FileDropzoneArea onFileUpload={props.onBackgroundFileUpload} />
          </AccordionDetails>
        </Accordion>

        {/* ── Bio ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><FingerprintIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.bioLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
            <TextField
              fullWidth
              multiline
              minRows={4}
              placeholder={translation.settings.bio}
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              sx={inputSx}
            />
          </AccordionDetails>
        </Accordion>

        {/* ── Social links ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><ShareIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.socialLinksLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 2, px: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {SOCIAL_PLATFORMS.map(({ key, label, Icon, color, placeholder }) => (
              <TextField
                key={key}
                fullWidth
                type="url"
                label={label}
                placeholder={placeholder}
                value={socialLinks[key] ?? ''}
                onChange={(e) => setSocialLinks((prev) => ({ ...prev, [key]: e.target.value }))}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon sx={{ color, fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={inputSx}
              />
            ))}
          </AccordionDetails>
        </Accordion>

        {/* ── Language ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><TranslateIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.languageLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
            <FormControl>
              <RadioGroup
                value={language}
                onChange={(e) => {
                  setLanguage(e.target.value);
                  handleChangeLanguage(e.target.value);
                }}
              >
                <FormControlLabel
                  value="pl"
                  control={<Radio size="small" sx={radioSx} />}
                  label={<Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>{translation.settings.languagePolish}</Typography>}
                />
                <FormControlLabel
                  value="en"
                  control={<Radio size="small" sx={radioSx} />}
                  label={<Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>{translation.settings.languageEnglish}</Typography>}
                />
              </RadioGroup>
            </FormControl>
          </AccordionDetails>
        </Accordion>

        {/* ── Default context ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><FreaklandIcon style={{ width: 20, height: 20, opacity: 0.55 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.contextLabel}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
            <FormControl fullWidth>
              <RadioGroup
                value={context}
                onChange={(e) => setContext(e.target.value)}
              >
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  {CONTEXTS.map(({ value, labelKey }) => (
                    <FormControlLabel
                      key={value}
                      value={value}
                      control={<Radio size="small" sx={radioSx} />}
                      label={<Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>{translation.settings[labelKey]}</Typography>}
                    />
                  ))}
                </Box>
              </RadioGroup>
            </FormControl>
          </AccordionDetails>
        </Accordion>

        {/* ── YouTube feeds ── */}
        <Accordion ref={youtubeFeedSectionRef} sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><YouTubeIcon sx={{ fontSize: 20, color: '#FF0000' }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              YouTube Feeds
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2 }}>
            <SocialListComponent
              social="youtube"
              isAddingFeed={isAddingFeed}
              forUsername={props.currentUser?.username}
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 1.5 }}>
              <Box sx={{ pt: 0.5, flexShrink: 0 }}>
                <IconButton
                  component={Link}
                  to="https://www.youtube.com"
                  target="_blank"
                  rel="noopener"
                  size="small"
                  sx={{ color: '#FF0000', p: 0.25 }}
                >
                  <YouTubeIcon sx={{ fontSize: 28 }} />
                </IconButton>
              </Box>
              <TextField
                size="small"
                label={translation.linksYoutube.placeholders.addFeed}
                variant="outlined"
                fullWidth
                value={youTubeFeed}
                placeholder="@ExampleChannel"
                onChange={(e) => validateYouTubeFeed(e.target.value, setIsValidYouTubeFeed, setYouTubeFeed)}
                error={!isValidYouTubeFeed}
                helperText={!isValidYouTubeFeed ? translation.linksYoutube.placeholders.invalidFeed : undefined}
                sx={inputSx}
              />
              <IconButton
                size="small"
                onClick={() => { setYouTubeFeed(''); setIsValidYouTubeFeed(true); }}
                sx={{ mt: 0.5, color: 'rgba(255,255,255,0.3)', '&:hover': { color: '#f87171' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={!youTubeFeed || !isValidYouTubeFeed}
                onClick={() => addFeedYouTube(youTubeFeed)}
                sx={{
                  mt: 0.5,
                  color: (!youTubeFeed || !isValidYouTubeFeed) ? DEFAULT_THEME_COLOR_GREY : DEFAULT_THEME_COLOR,
                  '&:hover': { color: DEFAULT_THEME_COLOR },
                  '&.Mui-disabled': { color: DEFAULT_THEME_COLOR_GREY },
                }}
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* ── Blocked users ── */}
        <Accordion sx={accordionSx} TransitionProps={{ unmountOnExit: true }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} data-testid="settings-blocked-users">
            <SectionIcon><BlockIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Box>
              <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
                {translation.userBlock.blockedUsers}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>
                {translation.userBlock.blockedUsersDescription}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2 }}>
            <BlockedUsersList />
          </AccordionDetails>
        </Accordion>

        {/* ── Notifications ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><NotificationsActiveIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.notifications}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
            <Button
              variant="contained"
              startIcon={<NotificationsActiveIcon />}
              onClick={setupNotifications}
              sx={{
                background: DEFAULT_GRADIENT,
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { background: DEFAULT_GRADIENT_HOVER },
              }}
            >
              {translation.settings.notifications}
            </Button>
            <Typography sx={{ mt: 2, color: 'white', fontWeight: 500, fontSize: '0.85rem' }}>
              {translation.settings.customNotifications}
            </Typography>
            <Typography sx={{ mb: 0.5, color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
              {translation.settings.customNotificationsDescription}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
              {CUSTOM_NOTIFICATIONS.map((notification) => (
                <FormControlLabel
                  key={notification.key}
                  sx={{ minHeight: 44, mr: 0 }}
                  control={
                    <Switch
                      checked={!!customNotifications[notification.key]}
                      onChange={(e) => handleCustomNotificationToggle(notification, e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: DEFAULT_THEME_COLOR },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: DEFAULT_THEME_COLOR },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                      {translation.settings[notification.labelKey]}
                    </Typography>
                  }
                />
              ))}
            </Box>
            <Typography sx={{ mt: 2, mb: 0.5, color: 'white', fontWeight: 500, fontSize: '0.85rem' }}>
              {freaksT('theFreaks.title')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 2 }}>
              {FREAKS_PREFERENCES.map((preference) => (
                <FormControlLabel
                  key={preference.key}
                  data-testid={`settings-${preference.key}`}
                  sx={{ minHeight: 44, mr: 0 }}
                  control={
                    <Switch
                      checked={!!freaksPreferences[preference.key]}
                      onChange={(e) => handleFreaksPreferenceToggle(preference, e.target.checked)}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: DEFAULT_THEME_COLOR },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: DEFAULT_THEME_COLOR },
                      }}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem' }}>
                        {freaksT(preference.labelKey)}
                      </Typography>
                      {preference.descriptionKey ? (
                        <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem' }}>
                          {freaksT(preference.descriptionKey)}
                        </Typography>
                      ) : null}
                    </Box>
                  }
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* ── Theme ── */}
        <Accordion sx={accordionSx}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <SectionIcon><ContrastIcon sx={{ fontSize: 20 }} /></SectionIcon>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.9rem' }}>
              {translation.settings.theme}
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.5, pb: 2 }}>
            <Button
              variant="contained"
              startIcon={themeMode === 'dark' ? <WbSunnyIcon /> : <DarkModeIcon />}
              onClick={handleThemeToggle}
              sx={{
                background: DEFAULT_GRADIENT,
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: 2,
                '&:hover': { background: DEFAULT_GRADIENT_HOVER },
              }}
            >
              {(!themeMode || themeMode === 'dark') ? translation.settings.switchThemeLight : translation.settings.switchThemeDark}
            </Button>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 3, borderColor: 'rgba(255,255,255,0.07)' }} />

        {/* ── Logout ── */}
        <Button
          variant="outlined"
          onClick={() => { props.onClickLogout(); navigate('/login'); }}
          sx={{
            borderColor: '#f87171',
            color: '#f87171',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 2,
            '&:hover': { borderColor: '#ef4444', background: 'rgba(248,113,113,0.08)', color: '#ef4444' },
          }}
        >
          {translation.settings.logout}
        </Button>

        <Box sx={{ mt: 4, pb: 6 }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.75rem' }}>
            {translation.settings.version}: {packageJson.version}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const mapStateToProps = state => ({
  ...state.settings,
  currentUser: state.common.currentUser,
  image: state.settings.image,
  backgroundImage: state.settings.backgroundImage,
  leftNavigationOpen: state.common.leftNavigationOpen,
});

const mapDispatchToProps = dispatch => ({
  onClickLogout: () => dispatch({ type: LOGOUT }),
  onSubmitForm: user => dispatch({ type: SETTINGS_SAVED, payload: agent.Auth.save(user) }),
  onThemeModeChange: newThemeMode => dispatch({ type: THEME_MODE_CHANGE, payload: { newThemeMode } }),
  onUnload: () => dispatch({ type: SETTINGS_PAGE_UNLOADED }),
  onFileUpload: async payload => {
    if (payload && payload.length) {
      const image = await convertToBase64(payload[0]);
      dispatch({ type: UPLOAD_USER_IMAGE, payload: { image } });
    }
  },
  onBackgroundFileUpload: async payload => {
    if (payload && payload.length) {
      const backgroundImage = await convertToBase64(payload[0]);
      dispatch({ type: UPLOAD_USER_BACKGROUND_IMAGE, payload: { backgroundImage } });
    }
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
